/**
 * Business Value Calculator - Benefit Calculators
 * Sprint 7: Benefits Tab Implementation
 *
 * Formula implementations for 12 Level 1 benefit drivers
 * Based on Pistoia Alliance calculator patterns, adapted for Compass hierarchy
 */

// ============================================================================
// TYPES
// ============================================================================

export interface CalculatorParameters {
  [key: string]: number;
}

export interface CalculationResult {
  year1: number;
  year2: number;
  year3: number;
  total: number; // Sum of 3 years
  details: {
    [key: string]: number | string;
  };
}

export interface BenefitCalculator {
  driverId: string;
  driverLabel: string;
  description: string;
  formula: string;
  parameters: ParameterDefinition[];
  defaultParameters: CalculatorParameters;
  calculate: (params: CalculatorParameters) => CalculationResult;
}

export interface ParameterDefinition {
  name: string;
  label: string;
  type: 'number' | 'percentage';
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  tooltip?: string;
}

// ============================================================================
// TRUSTED DATA CALCULATORS (3)
// ============================================================================

/**
 * 1. AI_Ready_Data Calculator
 * Formula: savings = workflows_automated × time_per_workflow × loaded_cost_per_day
 */
export const AI_Ready_Data_Calculator: BenefitCalculator = {
  driverId: 'AI_Ready_Data',
  driverLabel: 'AI-Ready Data Growth',
  description: 'Quantify time savings from increasing machine-actionable data for AI/ML workflows',
  formula: 'savings = workflows_automated × time_per_workflow × loaded_cost_per_day',

  parameters: [
    {
      name: 'machine_actionable_baseline',
      label: 'Machine Actionable (Baseline)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Current percentage of datasets that are machine-actionable'
    },
    {
      name: 'machine_actionable_FAIR',
      label: 'Machine Actionable (FAIR)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Target percentage after FAIR implementation'
    },
    {
      name: 'workflows_per_year',
      label: 'AI/ML Workflows per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of AI/ML workflows executed annually'
    },
    {
      name: 'time_per_manual_workflow',
      label: 'Time per Manual Workflow',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Days required for manual data preparation per workflow'
    },
    {
      name: 'loaded_cost_per_day',
      label: 'Loaded Cost per Day',
      type: 'number',
      unit: '$',
      min: 0,
      step: 50,
      tooltip: 'Fully-loaded daily cost of data scientists/engineers'
    }
  ],

  defaultParameters: {
    machine_actionable_baseline: 15,
    machine_actionable_FAIR: 80,
    workflows_per_year: 1200,
    time_per_manual_workflow: 2,
    loaded_cost_per_day: 800
  },

  calculate: (params) => {
    const {
      machine_actionable_baseline = 15,
      machine_actionable_FAIR = 80,
      workflows_per_year = 1200,
      time_per_manual_workflow = 2,
      loaded_cost_per_day = 800
    } = params;

    const efficiencyGainPercent = ((machine_actionable_FAIR - machine_actionable_baseline) / machine_actionable_baseline) * 100;
    const workflowsAutomated = workflows_per_year * ((machine_actionable_FAIR - machine_actionable_baseline) / 100);
    const annualSavings = workflowsAutomated * time_per_manual_workflow * loaded_cost_per_day;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.3); // 30% growth as ML adoption increases
    const year3 = Math.round(annualSavings * 1.6); // 60% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        efficiency_gain_percent: Math.round(efficiencyGainPercent * 10) / 10,
        workflows_automated: Math.round(workflowsAutomated),
        annual_savings: annualSavings
      }
    };
  }
};

/**
 * 2. Data_quality Calculator
 * Formula: savings = errors_avoided × cost_per_error
 */
export const Data_quality_Calculator: BenefitCalculator = {
  driverId: 'Data_quality',
  driverLabel: 'Data Quality Improvement',
  description: 'Calculate cost savings from reducing data quality errors',
  formula: 'savings = errors_avoided × cost_per_error',

  parameters: [
    {
      name: 'baseline_error_rate',
      label: 'Error Rate (Baseline)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 0.1,
      tooltip: 'Current error rate in datasets'
    },
    {
      name: 'FAIR_error_rate',
      label: 'Error Rate (FAIR)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 0.1,
      tooltip: 'Target error rate after FAIR implementation'
    },
    {
      name: 'datasets_affected',
      label: 'Datasets Affected',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of datasets impacted by quality improvements'
    },
    {
      name: 'cost_per_error',
      label: 'Cost per Error',
      type: 'number',
      unit: '$',
      min: 0,
      step: 50,
      tooltip: 'Average cost to identify and fix each data error'
    }
  ],

  defaultParameters: {
    baseline_error_rate: 10,
    FAIR_error_rate: 4,
    datasets_affected: 1000,
    cost_per_error: 900
  },

  calculate: (params) => {
    const {
      baseline_error_rate = 10,
      FAIR_error_rate = 4,
      datasets_affected = 1000,
      cost_per_error = 900
    } = params;

    const errorReductionPercent = ((baseline_error_rate - FAIR_error_rate) / baseline_error_rate) * 100;
    const errorsAvoided = datasets_affected * ((baseline_error_rate - FAIR_error_rate) / 100);
    const annualSavings = errorsAvoided * cost_per_error;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.2); // 20% growth
    const year3 = Math.round(annualSavings * 1.4); // 40% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        error_reduction_percent: Math.round(errorReductionPercent * 10) / 10,
        errors_avoided: Math.round(errorsAvoided),
        annual_savings: annualSavings
      }
    };
  }
};

/**
 * 3. Risk_and_compliance Calculator
 * Formula: savings = compliance_violations_avoided × avg_fine + audit_hours_saved × hourly_cost
 */
export const Risk_and_compliance_Calculator: BenefitCalculator = {
  driverId: 'Risk_and_compliance',
  driverLabel: 'Risk & Compliance Improvement',
  description: 'Estimate savings from reduced regulatory risk and audit burden',
  formula: 'savings = violations_avoided × avg_fine + audit_hours_saved × hourly_cost',

  parameters: [
    {
      name: 'violations_per_year_baseline',
      label: 'Violations per Year (Baseline)',
      type: 'number',
      min: 0,
      step: 0.1,
      tooltip: 'Average compliance violations per year currently'
    },
    {
      name: 'violations_per_year_FAIR',
      label: 'Violations per Year (FAIR)',
      type: 'number',
      min: 0,
      step: 0.1,
      tooltip: 'Expected violations after FAIR implementation'
    },
    {
      name: 'average_regulatory_fine',
      label: 'Average Regulatory Fine',
      type: 'number',
      unit: '$',
      min: 0,
      step: 1000,
      tooltip: 'Average fine amount per compliance violation'
    },
    {
      name: 'audit_hours_saved_per_year',
      label: 'Audit Hours Saved per Year',
      type: 'number',
      unit: 'hours',
      min: 0,
      step: 10,
      tooltip: 'Hours saved on compliance audits due to better data governance'
    },
    {
      name: 'audit_hourly_cost',
      label: 'Audit Hourly Cost',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10,
      tooltip: 'Hourly cost of compliance audit resources'
    }
  ],

  defaultParameters: {
    violations_per_year_baseline: 3,
    violations_per_year_FAIR: 0.5,
    average_regulatory_fine: 250000,
    audit_hours_saved_per_year: 200,
    audit_hourly_cost: 150
  },

  calculate: (params) => {
    const {
      violations_per_year_baseline = 3,
      violations_per_year_FAIR = 0.5,
      average_regulatory_fine = 250000,
      audit_hours_saved_per_year = 200,
      audit_hourly_cost = 150
    } = params;

    const violationsAvoided = violations_per_year_baseline - violations_per_year_FAIR;
    const fineSavings = violationsAvoided * average_regulatory_fine;
    const auditSavings = audit_hours_saved_per_year * audit_hourly_cost;
    const annualSavings = fineSavings + auditSavings;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.1); // 10% growth (conservative)
    const year3 = Math.round(annualSavings * 1.15); // 15% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        violations_avoided: Math.round(violationsAvoided * 10) / 10,
        fine_savings: fineSavings,
        audit_savings: auditSavings
      }
    };
  }
};

// ============================================================================
// SPEED CALCULATORS (3)
// ============================================================================

/**
 * 4. Time_to_product Calculator
 * Formula: value = months_saved × profit_per_month × products_in_pipeline
 */
export const Time_to_product_Calculator: BenefitCalculator = {
  driverId: 'Time_to_product',
  driverLabel: 'Faster Time to Product',
  description: 'Calculate revenue impact from accelerated product development',
  formula: 'value = months_saved × profit_per_month × products_in_pipeline',

  parameters: [
    {
      name: 'development_time_baseline',
      label: 'Development Time (Baseline)',
      type: 'number',
      unit: 'months',
      min: 0,
      step: 1,
      tooltip: 'Current average development time per product'
    },
    {
      name: 'development_time_FAIR',
      label: 'Development Time (FAIR)',
      type: 'number',
      unit: 'months',
      min: 0,
      step: 1,
      tooltip: 'Expected development time with FAIR data'
    },
    {
      name: 'profit_per_month_per_product',
      label: 'Profit per Month per Product',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10000,
      tooltip: 'Monthly profit generated by each product'
    },
    {
      name: 'products_in_pipeline_per_year',
      label: 'Products in Pipeline per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of products in development annually'
    }
  ],

  defaultParameters: {
    development_time_baseline: 24,
    development_time_FAIR: 21,
    profit_per_month_per_product: 600000,
    products_in_pipeline_per_year: 2
  },

  calculate: (params) => {
    const {
      development_time_baseline = 24,
      development_time_FAIR = 21,
      profit_per_month_per_product = 600000,
      products_in_pipeline_per_year = 2
    } = params;

    const monthsSaved = development_time_baseline - development_time_FAIR;
    const annualValue = monthsSaved * profit_per_month_per_product * products_in_pipeline_per_year;

    const year1 = Math.round(annualValue);
    const year2 = Math.round(annualValue * 1.1); // 10% growth
    const year3 = Math.round(annualValue * 1.15); // 15% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        months_saved: monthsSaved,
        products_accelerated: products_in_pipeline_per_year,
        value_per_product: monthsSaved * profit_per_month_per_product
      }
    };
  }
};

/**
 * 5. Faster_decisions Calculator
 * Formula: savings = time_saved × loaded_cost_per_day × decisions_per_year
 */
export const Faster_decisions_Calculator: BenefitCalculator = {
  driverId: 'Faster_decisions',
  driverLabel: 'Faster Decision Making',
  description: 'Quantify value from accelerated data-driven decisions',
  formula: 'savings = time_saved × loaded_cost_per_day × decisions_per_year',

  parameters: [
    {
      name: 'decision_time_baseline',
      label: 'Decision Time (Baseline)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Current average time to make data-driven decisions'
    },
    {
      name: 'decision_time_FAIR',
      label: 'Decision Time (FAIR)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Expected decision time with FAIR data'
    },
    {
      name: 'loaded_cost_per_day',
      label: 'Loaded Cost per Day',
      type: 'number',
      unit: '$',
      min: 0,
      step: 50,
      tooltip: 'Fully-loaded daily cost of decision makers'
    },
    {
      name: 'critical_decisions_per_year',
      label: 'Critical Decisions per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of data-dependent decisions annually'
    }
  ],

  defaultParameters: {
    decision_time_baseline: 5,
    decision_time_FAIR: 2,
    loaded_cost_per_day: 800,
    critical_decisions_per_year: 50
  },

  calculate: (params) => {
    const {
      decision_time_baseline = 5,
      decision_time_FAIR = 2,
      loaded_cost_per_day = 800,
      critical_decisions_per_year = 50
    } = params;

    const timeSaved = decision_time_baseline - decision_time_FAIR;
    const annualSavings = timeSaved * loaded_cost_per_day * critical_decisions_per_year;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.15); // 15% growth
    const year3 = Math.round(annualSavings * 1.3); // 30% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        time_saved_per_decision: timeSaved,
        decisions_per_year: critical_decisions_per_year,
        savings_per_decision: timeSaved * loaded_cost_per_day
      }
    };
  }
};

/**
 * 6. Shorter_time_cycles Calculator
 * Formula: savings = cycles_per_year × time_saved_per_cycle × loaded_cost_per_day
 */
export const Shorter_time_cycles_Calculator: BenefitCalculator = {
  driverId: 'Shorter_time_cycles',
  driverLabel: 'Shorter Time Cycles',
  description: 'Calculate savings from reduced cycle times in recurring processes',
  formula: 'savings = cycles_per_year × time_saved_per_cycle × loaded_cost_per_day',

  parameters: [
    {
      name: 'cycle_time_baseline',
      label: 'Cycle Time (Baseline)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Current average cycle time for recurring processes'
    },
    {
      name: 'cycle_time_FAIR',
      label: 'Cycle Time (FAIR)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Expected cycle time with FAIR data'
    },
    {
      name: 'cycles_per_year',
      label: 'Cycles per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of process cycles executed annually'
    },
    {
      name: 'loaded_cost_per_day',
      label: 'Loaded Cost per Day',
      type: 'number',
      unit: '$',
      min: 0,
      step: 50,
      tooltip: 'Fully-loaded daily cost of resources'
    }
  ],

  defaultParameters: {
    cycle_time_baseline: 10,
    cycle_time_FAIR: 7,
    cycles_per_year: 100,
    loaded_cost_per_day: 600
  },

  calculate: (params) => {
    const {
      cycle_time_baseline = 10,
      cycle_time_FAIR = 7,
      cycles_per_year = 100,
      loaded_cost_per_day = 600
    } = params;

    const timeSavedPerCycle = cycle_time_baseline - cycle_time_FAIR;
    const annualSavings = cycles_per_year * timeSavedPerCycle * loaded_cost_per_day;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.2); // 20% growth
    const year3 = Math.round(annualSavings * 1.35); // 35% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        time_saved_per_cycle: timeSavedPerCycle,
        total_days_saved: timeSavedPerCycle * cycles_per_year,
        cycles_per_year: cycles_per_year
      }
    };
  }
};

// ============================================================================
// COST CALCULATORS (3)
// ============================================================================

/**
 * 7. Resource_Savings Calculator
 * Formula: savings = time_saved × cost_per_day × datasets_per_year
 */
export const Resource_Savings_Calculator: BenefitCalculator = {
  driverId: 'Resource_Savings',
  driverLabel: 'Resource Savings',
  description: 'Calculate cost savings from automated data preparation',
  formula: 'savings = time_saved × cost_per_day × datasets_per_year',

  parameters: [
    {
      name: 'data_prep_time_baseline',
      label: 'Data Prep Time (Baseline)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Current time to prepare each dataset'
    },
    {
      name: 'data_prep_time_FAIR',
      label: 'Data Prep Time (FAIR)',
      type: 'number',
      unit: 'days',
      min: 0,
      step: 0.5,
      tooltip: 'Expected prep time with FAIR standards'
    },
    {
      name: 'cost_per_day',
      label: 'Cost per Day',
      type: 'number',
      unit: '$',
      min: 0,
      step: 50,
      tooltip: 'Daily cost of data preparation resources'
    },
    {
      name: 'datasets_per_year',
      label: 'Datasets per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Number of datasets prepared annually'
    }
  ],

  defaultParameters: {
    data_prep_time_baseline: 10,
    data_prep_time_FAIR: 3,
    cost_per_day: 400,
    datasets_per_year: 300
  },

  calculate: (params) => {
    const {
      data_prep_time_baseline = 10,
      data_prep_time_FAIR = 3,
      cost_per_day = 400,
      datasets_per_year = 300
    } = params;

    const timeSaved = data_prep_time_baseline - data_prep_time_FAIR;
    const annualSavings = timeSaved * cost_per_day * datasets_per_year;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.25); // 25% growth
    const year3 = Math.round(annualSavings * 1.5); // 50% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        days_saved_per_dataset: timeSaved,
        total_days_saved: timeSaved * datasets_per_year,
        savings_per_dataset: timeSaved * cost_per_day
      }
    };
  }
};

/**
 * 8. Cost_savings Calculator
 * Formula: savings = (baseline_cost - FAIR_cost) × volume + licensing_reduction
 */
export const Cost_savings_Calculator: BenefitCalculator = {
  driverId: 'Cost_savings',
  driverLabel: 'Direct Cost Savings',
  description: 'Calculate direct cost reductions from infrastructure and licensing',
  formula: 'savings = (baseline_cost - FAIR_cost) × volume + licensing_reduction',

  parameters: [
    {
      name: 'storage_cost_per_TB_baseline',
      label: 'Storage Cost per TB (Baseline)',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10,
      tooltip: 'Current annual storage cost per terabyte'
    },
    {
      name: 'storage_cost_per_TB_FAIR',
      label: 'Storage Cost per TB (FAIR)',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10,
      tooltip: 'Expected cost with FAIR optimization'
    },
    {
      name: 'data_volume_TB',
      label: 'Data Volume',
      type: 'number',
      unit: 'TB',
      min: 0,
      step: 1,
      tooltip: 'Total data volume in terabytes'
    },
    {
      name: 'licensing_cost_reduction',
      label: 'Licensing Cost Reduction',
      type: 'number',
      unit: '$',
      min: 0,
      step: 1000,
      tooltip: 'Annual savings from consolidated/eliminated licenses'
    }
  ],

  defaultParameters: {
    storage_cost_per_TB_baseline: 500,
    storage_cost_per_TB_FAIR: 200,
    data_volume_TB: 100,
    licensing_cost_reduction: 50000
  },

  calculate: (params) => {
    const {
      storage_cost_per_TB_baseline = 500,
      storage_cost_per_TB_FAIR = 200,
      data_volume_TB = 100,
      licensing_cost_reduction = 50000
    } = params;

    const storageSavings = (storage_cost_per_TB_baseline - storage_cost_per_TB_FAIR) * data_volume_TB;
    const annualSavings = storageSavings + licensing_cost_reduction;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.15); // 15% growth
    const year3 = Math.round(annualSavings * 1.25); // 25% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        storage_savings: storageSavings,
        licensing_savings: licensing_cost_reduction,
        total_annual_savings: annualSavings
      }
    };
  }
};

/**
 * 9. Cost_avoidance Calculator
 * Formula: savings = projects_avoided × monthly_cost × months_saved
 */
export const Cost_avoidance_Calculator: BenefitCalculator = {
  driverId: 'Cost_avoidance',
  driverLabel: 'Cost Avoidance',
  description: 'Calculate savings from avoiding unsuccessful projects',
  formula: 'savings = projects_avoided × monthly_cost × months_saved',

  parameters: [
    {
      name: 'unsuccessful_projects_per_year',
      label: 'Unsuccessful Projects per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Projects terminated or failing annually'
    },
    {
      name: 'monthly_project_cost',
      label: 'Monthly Project Cost',
      type: 'number',
      unit: '$',
      min: 0,
      step: 1000,
      tooltip: 'Average monthly cost per project'
    },
    {
      name: 'months_saved_per_project',
      label: 'Months Saved per Project',
      type: 'number',
      unit: 'months',
      min: 0,
      step: 1,
      tooltip: 'Months saved by identifying failures earlier with FAIR data'
    }
  ],

  defaultParameters: {
    unsuccessful_projects_per_year: 3,
    monthly_project_cost: 100000,
    months_saved_per_project: 6
  },

  calculate: (params) => {
    const {
      unsuccessful_projects_per_year = 3,
      monthly_project_cost = 100000,
      months_saved_per_project = 6
    } = params;

    const annualSavings = unsuccessful_projects_per_year * monthly_project_cost * months_saved_per_project;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.1); // 10% growth
    const year3 = Math.round(annualSavings * 1.2); // 20% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        projects_terminated_early: unsuccessful_projects_per_year,
        savings_per_project: monthly_project_cost * months_saved_per_project,
        total_annual_savings: annualSavings
      }
    };
  }
};

// ============================================================================
// EFFECTIVENESS CALCULATORS (3)
// ============================================================================

/**
 * 10. Resource_Productivity Calculator
 * Formula: value = productivity_gain_% × FTEs × average_FTE_cost
 */
export const Resource_Productivity_Calculator: BenefitCalculator = {
  driverId: 'Resource_Productivity',
  driverLabel: 'Resource Productivity',
  description: 'Calculate value from increased analyst productivity',
  formula: 'value = productivity_gain_% × FTEs × average_FTE_cost',

  parameters: [
    {
      name: 'analysis_time_baseline',
      label: 'Analysis Time (Baseline)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Current percentage of time spent on analysis vs data prep'
    },
    {
      name: 'analysis_time_FAIR',
      label: 'Analysis Time (FAIR)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Expected analysis time with FAIR data'
    },
    {
      name: 'number_of_FTEs',
      label: 'Number of FTEs',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Full-time equivalents affected by productivity gain'
    },
    {
      name: 'average_FTE_cost',
      label: 'Average FTE Cost',
      type: 'number',
      unit: '$',
      min: 0,
      step: 1000,
      tooltip: 'Average annual fully-loaded cost per FTE'
    }
  ],

  defaultParameters: {
    analysis_time_baseline: 40,
    analysis_time_FAIR: 70,
    number_of_FTEs: 10,
    average_FTE_cost: 120000
  },

  calculate: (params) => {
    const {
      analysis_time_baseline = 40,
      analysis_time_FAIR = 70,
      number_of_FTEs = 10,
      average_FTE_cost = 120000
    } = params;

    const productivityGainPercent = ((analysis_time_FAIR - analysis_time_baseline) / analysis_time_baseline) * 100;
    const annualValue = (productivityGainPercent / 100) * number_of_FTEs * average_FTE_cost;

    const year1 = Math.round(annualValue);
    const year2 = Math.round(annualValue * 1.1); // 10% growth
    const year3 = Math.round(annualValue * 1.2); // 20% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        productivity_gain_percent: Math.round(productivityGainPercent * 10) / 10,
        fte_count: number_of_FTEs,
        value_per_fte: Math.round(annualValue / number_of_FTEs)
      }
    };
  }
};

/**
 * 11. Profit_Success_rate Calculator
 * Formula: value = success_rate_increase_% × projects_per_year × revenue_per_project
 */
export const Profit_Success_rate_Calculator: BenefitCalculator = {
  driverId: 'Profit_Success_rate',
  driverLabel: 'Improved Success Rate',
  description: 'Calculate revenue impact from higher project success rates',
  formula: 'value = success_rate_increase_% × projects_per_year × revenue_per_project',

  parameters: [
    {
      name: 'project_success_rate_baseline',
      label: 'Success Rate (Baseline)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Current project success rate'
    },
    {
      name: 'project_success_rate_FAIR',
      label: 'Success Rate (FAIR)',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Expected success rate with FAIR data'
    },
    {
      name: 'projects_per_year',
      label: 'Projects per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Total number of projects initiated annually'
    },
    {
      name: 'average_revenue_per_successful_project',
      label: 'Revenue per Successful Project',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10000,
      tooltip: 'Average revenue generated by each successful project'
    }
  ],

  defaultParameters: {
    project_success_rate_baseline: 25,
    project_success_rate_FAIR: 35,
    projects_per_year: 20,
    average_revenue_per_successful_project: 2000000
  },

  calculate: (params) => {
    const {
      project_success_rate_baseline = 25,
      project_success_rate_FAIR = 35,
      projects_per_year = 20,
      average_revenue_per_successful_project = 2000000
    } = params;

    const successRateIncrease = (project_success_rate_FAIR - project_success_rate_baseline) / 100;
    const additionalSuccessfulProjects = projects_per_year * successRateIncrease;
    const annualValue = additionalSuccessfulProjects * average_revenue_per_successful_project;

    const year1 = Math.round(annualValue);
    const year2 = Math.round(annualValue * 1.05); // 5% growth (conservative)
    const year3 = Math.round(annualValue * 1.1); // 10% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        success_rate_increase_percent: Math.round(successRateIncrease * 100 * 10) / 10,
        additional_successful_projects: Math.round(additionalSuccessfulProjects * 10) / 10,
        total_annual_value: annualValue
      }
    };
  }
};

/**
 * 12. Insights_and_innovation Calculator
 * Formula: savings = requests_eliminated × support_hours × hourly_cost
 */
export const Insights_and_innovation_Calculator: BenefitCalculator = {
  driverId: 'Insights_and_innovation',
  driverLabel: 'Insights & Innovation',
  description: 'Calculate value from self-service analytics adoption',
  formula: 'savings = requests_eliminated × support_hours × hourly_cost',

  parameters: [
    {
      name: 'ad_hoc_analysis_requests_per_year',
      label: 'Ad Hoc Analysis Requests per Year',
      type: 'number',
      min: 0,
      step: 1,
      tooltip: 'Annual ad hoc data requests from business users'
    },
    {
      name: 'self_service_adoption_rate',
      label: 'Self-Service Adoption Rate',
      type: 'percentage',
      unit: '%',
      min: 0,
      max: 100,
      step: 1,
      tooltip: 'Percentage of requests handled via self-service with FAIR data'
    },
    {
      name: 'support_hours_per_request',
      label: 'Support Hours per Request',
      type: 'number',
      unit: 'hours',
      min: 0,
      step: 0.5,
      tooltip: 'Average analyst hours required per ad hoc request'
    },
    {
      name: 'support_hourly_cost',
      label: 'Support Hourly Cost',
      type: 'number',
      unit: '$',
      min: 0,
      step: 10,
      tooltip: 'Hourly cost of analyst support'
    }
  ],

  defaultParameters: {
    ad_hoc_analysis_requests_per_year: 600,
    self_service_adoption_rate: 40,
    support_hours_per_request: 3,
    support_hourly_cost: 100
  },

  calculate: (params) => {
    const {
      ad_hoc_analysis_requests_per_year = 600,
      self_service_adoption_rate = 40,
      support_hours_per_request = 3,
      support_hourly_cost = 100
    } = params;

    const requestsEliminated = ad_hoc_analysis_requests_per_year * (self_service_adoption_rate / 100);
    const annualSavings = requestsEliminated * support_hours_per_request * support_hourly_cost;

    const year1 = Math.round(annualSavings);
    const year2 = Math.round(annualSavings * 1.2); // 20% growth
    const year3 = Math.round(annualSavings * 1.4); // 40% growth

    return {
      year1,
      year2,
      year3,
      total: year1 + year2 + year3,
      details: {
        requests_eliminated: Math.round(requestsEliminated),
        hours_saved: Math.round(requestsEliminated * support_hours_per_request),
        total_annual_savings: annualSavings
      }
    };
  }
};

// ============================================================================
// CALCULATOR REGISTRY
// ============================================================================

export const BENEFIT_CALCULATORS: Record<string, BenefitCalculator> = {
  AI_Ready_Data: AI_Ready_Data_Calculator,
  Data_quality: Data_quality_Calculator,
  Risk_and_compliance: Risk_and_compliance_Calculator,
  Time_to_product: Time_to_product_Calculator,
  Faster_decisions: Faster_decisions_Calculator,
  Shorter_time_cycles: Shorter_time_cycles_Calculator,
  Resource_Savings: Resource_Savings_Calculator,
  Cost_savings: Cost_savings_Calculator,
  Cost_avoidance: Cost_avoidance_Calculator,
  Resource_Productivity: Resource_Productivity_Calculator,
  Profit_Success_rate: Profit_Success_rate_Calculator,
  Insights_and_innovation: Insights_and_innovation_Calculator
};

/**
 * Get calculator for a specific driver
 */
export function getCalculatorForDriver(driverId: string): BenefitCalculator | null {
  return BENEFIT_CALCULATORS[driverId] || null;
}

/**
 * Get all calculator IDs
 */
export function getAllCalculatorIds(): string[] {
  return Object.keys(BENEFIT_CALCULATORS);
}

// ============================================================================
// LEVEL 0 PILLAR METADATA
// ============================================================================

export interface Level0Pillar {
  id: string;
  label: string;
  color: string;
  icon: string;
  description: string;
  driverIds: string[];
}

export const LEVEL_0_PILLARS: Record<string, Level0Pillar> = {
  Trusted_data: {
    id: 'Trusted_data',
    label: 'Trusted Data',
    color: '#10b981', // Green
    icon: '🛡️',
    description: 'Improve data quality, compliance, and AI/ML readiness',
    driverIds: ['AI_Ready_Data', 'Data_quality', 'Risk_and_compliance']
  },
  Speed: {
    id: 'Speed',
    label: 'Speed',
    color: '#3b82f6', // Blue
    icon: '⚡',
    description: 'Accelerate time-to-insight and decision velocity',
    driverIds: ['Time_to_product', 'Faster_decisions', 'Shorter_time_cycles']
  },
  Cost: {
    id: 'Cost',
    label: 'Cost',
    color: '#f59e0b', // Amber
    icon: '💰',
    description: 'Reduce operational costs and optimize resources',
    driverIds: ['Resource_Savings', 'Cost_savings', 'Cost_avoidance']
  },
  Effectiveness: {
    id: 'Effectiveness',
    label: 'Effectiveness',
    color: '#8b5cf6', // Purple
    icon: '🎯',
    description: 'Enhance productivity and success rates',
    driverIds: ['Resource_Productivity', 'Profit_Success_rate', 'Insights_and_innovation']
  }
};

/**
 * Get Level 0 pillar by ID
 */
export function getLevel0Pillar(pillarId: string): Level0Pillar | null {
  return LEVEL_0_PILLARS[pillarId] || null;
}

/**
 * Get Level 0 pillar for a specific driver
 */
export function getPillarForDriver(driverId: string): Level0Pillar | null {
  for (const pillar of Object.values(LEVEL_0_PILLARS)) {
    if (pillar.driverIds.includes(driverId)) {
      return pillar;
    }
  }
  return null;
}
